(() => {
  if (window.__RavinNotificationsLoaded) return;
  window.__RavinNotificationsLoaded = true;
  const instances = new WeakMap();
  const selector = '[data-ravin-notifications]';

  function init(root) {
    if (!root || instances.has(root)) return;
    const panel = root.querySelector('[data-rn-panel]');
    const count = root.querySelector('[data-rn-count]');
    if (!panel || !count) return;
    const controller = new AbortController();
    const threshold = Math.max(20, Number(root.dataset.rnThreshold) || 20);
    const gap = Math.max(0, Number(root.dataset.rnGap) || 12);
    const number = count.querySelector('.ravin-notifications__count-number');
    const word = count.querySelector('.ravin-notifications__count-word');
    const isArabic = document.documentElement.lang.toLowerCase().startsWith('ar');
    let openedAtY = Math.max(0, window.scrollY || 0);
    let expanded = false;
    let frame = 0;

    function visibleCards() {
      return [...panel.querySelectorAll('[data-rn-notice]:not([hidden])')];
    }

    function updateCount() {
      const remaining = visibleCards().length;
      if (number) number.textContent = String(remaining);
      if (word) word.textContent = isArabic ? (remaining === 1 ? 'إشعار' : 'إشعارات') : (remaining === 1 ? 'NOTIFICATION' : 'NOTIFICATIONS');
      count.setAttribute('aria-label', isArabic ? 'عرض ' + remaining + ' إشعارات ريفين' : 'Show ' + remaining + ' RAVIN notification' + (remaining === 1 ? '' : 's'));
      root.hidden = remaining === 0;
      if (remaining === 0) setExpanded(false);
    }

    function setExpanded(next) {
      expanded = Boolean(next);
      root.dataset.rnMode = expanded ? 'expanded' : 'collapsed';
      panel.inert = !expanded;
      panel.setAttribute('aria-hidden', String(!expanded));
      count.setAttribute('aria-expanded', String(expanded));
      const inGang = Boolean(root.closest('[data-gang-dock]'));
      count.setAttribute('aria-hidden', String(expanded && !inGang));
      count.tabIndex = expanded && !inGang ? -1 : 0;
    }

    // Shared floating-widget behavior: an outside tap or another widget opens
    // only minimizes this panel; no focus jump or click interception.
    root.addEventListener('ravin:float:close', () => {
      if (expanded) setExpanded(false);
    }, { signal: controller.signal });

    function updatePosition() {
      frame = 0;
      const isMobile = window.matchMedia('(max-width:749px)').matches;
      const vertical = isMobile ? root.dataset.rnMobileVertical : root.dataset.rnDesktopVertical;
      if (vertical === 'bottom') {
        root.style.removeProperty('--rn-top');
        return;
      }
      const property = isMobile ? '--rn-mobile-vertical-offset' : '--rn-desktop-vertical-offset';
      const extra = Number.parseFloat(getComputedStyle(root).getPropertyValue(property)) || 0;
      let top;
      if (vertical === 'top') {
        top = 8 + extra;
      } else {
        const header = document.querySelector('.section-header .header-wrapper') ||
                       document.querySelector('.section-header');
        const bottom = header ? header.getBoundingClientRect().bottom : 70;
        top = bottom + gap + extra;
      }
      const limited = Math.max(8, Math.min(window.innerHeight - 58, Math.round(top)));
      if (Number.isFinite(limited)) root.style.setProperty('--rn-top', limited + 'px');
    }

    function requestPosition() {
      if (!frame) frame = window.requestAnimationFrame(updatePosition);
    }

    function onScroll() {
      const y = Math.max(0, window.scrollY || document.documentElement.scrollTop || 0);
      if (expanded && Math.abs(y - openedAtY) >= threshold) {
        setExpanded(false);
      }
      requestPosition();
    }

    const collapseButton = root.querySelector('[data-rn-collapse]');
    collapseButton?.addEventListener('click', () => {
      if (!expanded) return;
      setExpanded(false);
      count.focus({ preventScroll: true });
    }, { signal: controller.signal });

    count.addEventListener('click', () => {
      if (expanded && root.closest('[data-gang-dock]')) { setExpanded(false); return; }
      if (expanded || root.hidden) return;
      openedAtY = Math.max(0, window.scrollY || document.documentElement.scrollTop || 0);
      setExpanded(true);
      requestPosition();
    }, { signal: controller.signal });

    root.addEventListener('click', event => {
      const dismiss = event.target.closest('[data-rn-dismiss]');
      if (!dismiss || !root.contains(dismiss)) return;
      event.preventDefault();
      event.stopPropagation();
      const card = dismiss.closest('[data-rn-notice]');
      if (!card) return;
      card.hidden = true;
      updateCount();
    }, { signal: controller.signal });

    root.addEventListener('keydown', event => {
      if (event.key === 'Escape' && expanded) {
        setExpanded(false);
        count.focus();
      }
    }, { signal: controller.signal });

    const section = root.closest('.shopify-section');
    if (section) {
      section.addEventListener('shopify:section:select', () => {
        openedAtY = Math.max(0, window.scrollY || 0);
        setExpanded(true);
      }, { signal: controller.signal });
      section.addEventListener('shopify:block:select', event => {
        if (root.contains(event.target)) {
          openedAtY = Math.max(0, window.scrollY || 0);
          setExpanded(true);
        }
      }, { signal: controller.signal });
    }

    window.addEventListener('scroll', onScroll, { passive: true, signal: controller.signal });
    window.addEventListener('resize', requestPosition, { passive: true, signal: controller.signal });
    window.addEventListener('pageshow', () => {
      openedAtY = Math.max(0, window.scrollY || 0);
      updateCount();
      // Restored pages should also show only the compact notification counter.
      setExpanded(false);
      requestPosition();
    }, { signal: controller.signal });

    updateCount();
    setExpanded(false);
    requestPosition();
    instances.set(root, () => {
      controller.abort();
      if (frame) window.cancelAnimationFrame(frame);
      instances.delete(root);
    });
  }

  function initIn(scope) {
    if (scope.matches?.(selector)) init(scope);
    scope.querySelectorAll?.(selector).forEach(init);
  }

  function unloadIn(scope) {
    const elements = [];
    if (scope.matches?.(selector)) elements.push(scope);
    scope.querySelectorAll?.(selector).forEach(el => elements.push(el));
    elements.forEach(el => instances.get(el)?.());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initIn(document), { once: true });
  } else {
    initIn(document);
  }
  document.addEventListener('shopify:section:load', event => initIn(event.target));
  document.addEventListener('shopify:section:unload', event => unloadIn(event.target));
})();
